import React, { useState } from 'react';
import PageHero from '../components/PageHero';
import CtaSection from '../components/CtaSection';
import Accordion from '../components/Accordion';
import TechDocLayout, { DocSection } from '../components/TechDocLayout';
import { CodePanel, Statement } from '../components/Statement';
import { Label } from '../components/System';
import Icon from '../components/Icon';
import useSeo from '../lib/useSeo';

const WORKFLOW_PROBLEMS = [
  {
    id: 'variance',
    title: 'Variance Investigation',
    currentState: 'Hours spent pivoting actuals against budget across cost centers, building cascading spreadsheets, chasing discrepancies',
    withSidwell: '2-minute answer with full GL lineage and audit trail',
    stat: '98% faster turnaround',
  },
  {
    id: 'close',
    title: 'Close Reconciliation',
    currentState: 'Days spent matching GL entries to sub-ledgers, manually tracing discrepancies, building reconciliation files',
    withSidwell: 'Queries that surface mismatches instantly, compliance-ready proof included',
    stat: 'Zero manual matching',
  },
  {
    id: 'reforecast',
    title: 'Reforecast Building',
    currentState: 'Re-pulling data from three systems, re-cleaning in spreadsheets, re-calculating, re-validating',
    withSidwell: 'Live numbers from source, no manual re-entry, version control built-in',
    stat: 'Live source ingestion',
  },
  {
    id: 'audit',
    title: 'Audit Preparation',
    currentState: 'Building reconciliation files, proving completeness, tracing numbers back to GL, rebuilding logic for auditors',
    withSidwell: 'Full lineage and calculation proof generated automatically with every answer',
    stat: 'Audit-ready on delivery',
  },
];

const PATHS = [
  {
    id: 'specialized',
    title: 'Specialized Financial Path',
    badge: 'OPTIMIZED LEDGER PATH',
    detail: 'Reserved for built-in transactions/budgets data. An LLM planner emits a structured FinancialAnalysisPlan—variance, driver analysis, waterfall, completeness checks—optimized for the ledger schema.',
    codeSnippet: `# Specialized Financial Path
def route(question, dataset):
    if dataset.is_sample:
        # LLM planner emits specialized FinancialAnalysisPlan
        return FinancialAnalysisPlan(
            metric="operating_expense",
            breakdown="cost_center",
            time_dimension="Q3_2026",
            calculations=["variance", "driver_waterfall"]
        )`,
  },
  {
    id: 'generic',
    title: 'Generic Schema-Agnostic Path',
    badge: 'DYNAMIC ANY-SCHEMA PATH',
    detail: "Every other dataset. The planner builds its prompt from the dataset's real fields, semantic roles, and field relationships. Works across Postgres GL, Snowflake ledgers, custom ledgers, or any tabular data.",
    codeSnippet: `# Generic Schema-Agnostic Path
def route(question, dataset):
    # Dynamic prompt generated from live schema metadata & semantic roles
    schema_prompt = build_schema_prompt(dataset.fields, dataset.sample_values)
    return AnalysisPlan(
        target_columns=dataset.get_measures(),
        group_by=dataset.get_dimensions(),
        time_filter=dataset.infer_date_range()
    )`,
  },
];

const ANALYSIS_TYPES = [
  'total',
  'breakdown',
  'ranking',
  'trend',
  'variance',
  'driver_analysis',
  'waterfall',
  'completeness',
];

const SEMANTIC_ROLES = [
  {
    role: 'Dimension',
    badge: 'GROUPING',
    examples: 'department_name, region, vendor, cost_center',
    desc: 'Categorical attributes used for filtering, segmentation, and multi-dimensional aggregations.',
    example: 'Selecting Dimension makes a column eligible for GROUP BY — e.g. "variance by cost_center" groups every row by this field.',
  },
  {
    role: 'Measure',
    badge: 'METRICS',
    examples: 'actual_amount, budget_amount, revenue, headcount',
    desc: 'Numeric quantitative fields that can be summarized, compared, or formulated into KPIs.',
    example: 'Selecting Measure makes a column summable — e.g. SUM(actual_amount) inside the pushdown query the planner emits.',
  },
  {
    role: 'Date',
    badge: 'TEMPORAL',
    examples: 'transaction_date, period_start, fiscal_year_end',
    desc: 'Time-series anchor columns for period-over-period, YTD, and multi-quarter trend calculations.',
    example: 'Selecting Date anchors period filters — e.g. WHERE transaction_date BETWEEN the inferred Q3 start and end.',
  },
  {
    role: 'GL Account',
    badge: 'FINANCIAL',
    examples: 'account_number, account_code, sub_account_id',
    desc: 'Chart-of-accounts structure keys mapping to balance sheet and income statement items.',
    example: 'Selecting GL Account maps rows into the chart-of-accounts hierarchy for balance sheet and income statement rollups.',
  },
  {
    role: 'Identifier',
    badge: 'ENTITY KEY',
    examples: 'dept_id, transaction_id, journal_entry_id',
    desc: 'Unique transaction or journal entry keys required for lineage tracing and deduplication.',
    example: 'Selecting Identifier flags a column as a lineage key — used to trace a result back to its source journal entry, never aggregated.',
  },
  {
    role: 'Ignore',
    badge: 'SYSTEM',
    examples: 'internal keys, audit columns, checksums',
    desc: 'Non-analytical system columns excluded from planner consideration to optimize prompt context.',
    example: 'Selecting Ignore removes a column from planner consideration entirely — it never enters a prompt or a query.',
  },
];

const RESPONSE_COMPONENTS = [
  {
    key: 'Text',
    badge: 'NARRATIVE',
    title: 'Grounded Executive Narrative',
    desc: 'Your question answered in clear business language, synthesized directly from the verified calculation results with zero fabricated figures.',
  },
  {
    key: 'Data',
    badge: 'PAYLOAD',
    title: 'Chart & Table Payload',
    desc: 'Structured analytical payload ready for immediate board presentations, audit workpapers, or executive reporting decks.',
  },
  {
    key: 'Plan',
    badge: 'INTENT',
    title: 'Compiled Analysis Plan',
    desc: 'The complete plan showing the analytical path selected, inferred semantic roles, and how the underlying schema was interpreted.',
  },
  {
    key: 'Pushdown SQL',
    badge: 'TRANSPARENCY',
    title: 'Exact Pushdown SQL',
    desc: 'The verbatim parameterised query executed against your GL or ledger. Every number on screen traces directly to this query.',
  },
];

const NARRATIVE_EXAMPLE =
  '“Q3 operating expense variance is driven primarily by cost center 4410 (Engineering), $184K over budget — see the pushdown query and row-level trace below.”';

const AUDIT_METADATA_ITEMS = [
  { label: 'Trace ID', value: 'trc_9a4f210d_q3var', hint: 'Unique trace identifier for support & auditing', component: 'Plan' },
  { label: 'Execution Mode', value: 'specialized_financial_engine', hint: 'Specialized ledger or schema-agnostic pushdown', component: 'Plan' },
  { label: 'Rows Egressed', value: '5 summary rows', hint: 'Minimal data transferred; raw tables remain in place', component: 'Data' },
  { label: 'Query Duration', value: '31ms execution', hint: 'Sub-second compute executed in your source engine', component: 'Pushdown SQL' },
  { label: 'Authorization Scope', value: 'org:acme_advisory', hint: 'Strict tenant (org) isolation', component: 'Plan' },
];

const METADATA_ENTITIES = [
  { name: 'Organization', detail: 'The firm’s account—every source, dataset, and chat message is strictly scoped by org_id.' },
  { name: 'User', detail: 'Firebase UID, auto-provisioned into an Organization on first sign-in.' },
  { name: 'DataSource', detail: 'Connection config as JSON; the secret is Fernet-encrypted and never serialized to clients.' },
  { name: 'Dataset', detail: 'A registered table/view—the unit a question is asked against.' },
  { name: 'Field', detail: 'The semantic layer—one row per column, carrying its inferred (and editable) role.' },
  { name: 'ChatMessageRow', detail: 'Full investigation history—prompt, narrative, plan, pushdown SQL, and audit metadata.' },
];

const ROADMAP_PHASES = [
  {
    phase: 'Now (Shipped)',
    status: 'COMPLETED',
    items: [
      'Real GL connectors (Postgres, MySQL) replacing simulated data',
      'Deterministic pushdown execution with full audit trail',
      'Schema-agnostic planner that works across data shapes',
      'Tenant-scoped auth with encrypted credentials',
      'Full query and result audit log',
    ],
  },
  {
    phase: 'This Quarter',
    status: 'IN PROGRESS',
    items: [
      'Multi-tenant production deployment (single-node hardened for uptime)',
      'Dedicated inference tier for finance-domain planner (off free-tier API key)',
      'Snowflake connector parity with existing Postgres / MySQL pushdown paths',
      'Early customer pilots with real FP&A workflows and variance investigations',
      'Performance baseline: sub-5s response times for 90th percentile queries',
    ],
  },
  {
    phase: 'Next 6 Months',
    status: 'PLANNED',
    items: [
      'Scale to 20+ GL connector types: QuickBooks, Xero, NetSuite, Adaptive, ADP Payroll, Workday, custom ledgers',
      'Multi-node deployment for enterprise throughput (100+ concurrent users)',
      'Close automation workflows: automated reconciliation detection, variance alerts, reforecast data pull',
      'Live GL integration so questions update with every transaction',
      'Audit workflows: pre-built templates for SOX testing, period-end close validation, intercompany reconciliation',
    ],
  },
  {
    phase: 'Vision (12+ Months)',
    status: 'NORTH STAR',
    items: [
      'Finance operations platform: Not just analysis, but orchestration. Close automation, journal posting, forecast refresh, compliance reporting—all auditable, all deterministic',
      'Industry-specific ledger interpreters: Auto-understand QuickBooks chart of accounts vs. NetSuite GL structures vs. custom hierarchies',
      'Real-time close: Finance teams get intra-period variance visibility, not just month-end reconciliation',
    ],
  },
];

const VERIFIED_SQL = `SELECT cost_center,
       SUM(actual_amount) AS actuals,
       SUM(budget_amount) AS budget,
       SUM(actual_amount - budget_amount) AS variance
FROM general_ledger_entries
WHERE fiscal_quarter = 'Q3-2026'
GROUP BY cost_center
ORDER BY variance DESC;`;

const SECTIONS = [
  { id: 'in-plain-terms', label: 'In plain terms' },
  { id: 'the-problem', label: 'The problem' },
  { id: 'orchestration', label: 'Orchestration' },
  { id: 'semantic-layer', label: 'The semantic layer' },
  { id: 'auditability', label: 'Auditability & governance' },
  { id: 'tenancy', label: 'Tenancy & security' },
  { id: 'where-we-are', label: 'Where we are' },
  { id: 'market-positioning', label: 'Market positioning' },
];

const HEADLINE_STATS = [
  { value: '60–70%', label: 'Time spent on manual data hygiene' },
  { value: '2 Min', label: 'Average Sidwell inquiry turnaround' },
  { value: '100%', label: 'Lineage down to source GL entries' },
  { value: 'Zero', label: 'Arithmetic performed by the LLM' },
];

// A selectable ruled row: the shared control for every picker on this page.
function PickRow({ on, onClick, dark = false, children, current = false }) {
  const idle = dark ? 'hover:bg-spandrel/60' : 'hover:bg-white';
  const active = dark ? 'bg-spandrel' : 'bg-white';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={current ? undefined : on}
      aria-current={current && on ? 'step' : undefined}
      className={`relative block min-h-[48px] w-full px-4 py-3.5 text-left transition-colors ${on ? active : idle}`}
    >
      <span aria-hidden="true" className={`absolute inset-y-0 left-0 w-[3px] ${on ? 'bg-bronze' : 'bg-transparent'}`} />
      {children}
    </button>
  );
}

function Compare({ eyebrow, title, intro, items, callout, calloutBody, result }) {
  return (
    <article className="border-t-2 border-wall pt-5">
      <Label className="mb-3">{eyebrow}</Label>
      <h3 className="type-h3 mb-3">{title}</h3>
      <p className="mb-3 text-glass">{intro}</p>
      <div className="border-t border-wall/20">
        {items.map((item) => (
          <Accordion key={item.title} title={item.title} detail={item.detail} headingLevel="h4" />
        ))}
      </div>
      <div className="mt-5 bg-glass-tint px-4 py-4">
        <h4 className="mb-1 font-sans text-[15px] font-semibold">{callout}</h4>
        <p className="text-[15px] text-glass-deep">{calloutBody}</p>
      </div>
      <p className="mt-5 border-t border-wall/20 pt-3 text-[15px] text-glass">
        <strong className="font-semibold text-wall">The Result:</strong> {result}
      </p>
    </article>
  );
}

function Innovation() {
  const [activeProblem, setActiveProblem] = useState(0);
  const [activePath, setActivePath] = useState(0);
  const [activeRole, setActiveRole] = useState(0);
  const [activeComponent, setActiveComponent] = useState(0);
  const [activePhase, setActivePhase] = useState(0);

  const problem = WORKFLOW_PROBLEMS[activeProblem];
  const activeComponentKey = RESPONSE_COMPONENTS[activeComponent].key;
  const phase = ROADMAP_PHASES[activePhase];

  // Parts of the specimen below light up when their response component is selected.
  const lit = (key) => (key === activeComponentKey ? 'bg-spandrel shadow-[inset_3px_0_0_0_theme(colors.bronze.DEFAULT)]' : '');

  useSeo({
    title: 'Innovation & Architecture',
    path: '/innovation',
    description:
      'Finance operations without the manual grind. Two planning paths, one deterministic core, auto-mapped semantic layers, and 100% auditable pushdown SQL.',
  });

  return (
    <main className="relative w-full">
      <PageHero
        variant="memo"
        image="tower"
        eyebrow="Engineering & R&D"
        title="Finance without the grind."
        subtitle="Finance teams lose most of their time pulling and matching data. By the time insights are ready, they’re stale."
        badge="Exact by design"
      />

      <TechDocLayout sections={SECTIONS}>
        <DocSection id="in-plain-terms">
          <Statement title="In plain terms" meta="No more manual matching">
            <div className="border-t border-wall/70 px-5 py-6 sm:px-7">
              <h2 className="type-h3 mb-3">Let the machine do the grind.</h2>
              <p className="type-body text-glass">
                You ask a question in plain English—<span className="bg-limestone px-1 text-wall">&ldquo;Variance between budget and actual by cost center for Q3&rdquo;</span>—and get the answer, the calculation, and proof. No spreadsheets.
              </p>
            </div>
            <dl className="m-0 grid grid-cols-2 border-t border-stone md:grid-cols-4">
              {HEADLINE_STATS.map((stat) => (
                <div key={stat.label} className="border-b border-l border-stone px-5 py-5 [&:nth-child(2n+1)]:border-l-0 md:border-b-0 md:[&:nth-child(2n+1)]:border-l md:first:!border-l-0">
                  <dd className="m-0 font-display text-[clamp(26px,2.4vw,34px)] font-light leading-none figure">{stat.value}</dd>
                  <dt className="mt-2 text-[14px] leading-snug text-glass">{stat.label}</dt>
                </div>
              ))}
            </dl>
            <p className="border-t border-stone px-5 py-3 text-[14px] text-glass sm:px-7">
              <strong className="font-semibold text-wall">Exact by design.</strong> Every number is traceable and checkable.
            </p>
          </Statement>
        </DocSection>

        <DocSection id="the-problem" label="The problem" title="What manual work costs" intro="Pick a task. See the difference.">
          <div className="grid grid-cols-1 border-y border-wall/25 md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
            <div role="group" aria-label="Finance tasks" className="md:border-r md:border-wall/25">
              {WORKFLOW_PROBLEMS.map((prob, idx) => (
                <div key={prob.id} className="border-b border-wall/15 last:border-b-0">
                  <PickRow on={activeProblem === idx} onClick={() => setActiveProblem(idx)}>
                    <span className="block font-semibold">{prob.title}</span>
                    <span className="mt-0.5 block text-[14px] text-glass">{prob.stat}</span>
                  </PickRow>
                </div>
              ))}
            </div>
            <div key={problem.id} className="swap-in border-t border-wall/25 bg-white px-5 py-5 md:border-t-0 sm:px-6">
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <h3 className="type-h3">{problem.title}</h3>
                <span className="text-[14px] font-semibold text-patina">{problem.stat}</span>
              </div>
              <h4 className="label mb-1.5 mt-5 font-sans text-oxide">Current State (Manual Grind)</h4>
              <p className="text-glass">{problem.currentState}</p>
              <h4 className="label mb-1.5 mt-5 border-t border-stone pt-4 font-sans text-patina">With Sidwell</h4>
              <p>{problem.withSidwell}</p>
            </div>
          </div>
          <div className="on-dark mt-8 bg-wall px-5 py-6 text-limestone sm:px-7">
            <Label dark className="mb-2">The Root Cause</Label>
            <p className="text-[clamp(18px,1.5vw,21px)] leading-snug">
              <strong className="font-semibold">Finance data is scattered, fragmented, and manual.</strong> Intelligence lives in spreadsheets, not systems. Sidwell fixes this by making your GL and budget systems the single source of truth—and making analysis instant.
            </p>
            <p className="mt-4 border-t border-limestone/20 pt-3 text-[14px] text-glass-light">Single Source of Truth</p>
          </div>
        </DocSection>

        <DocSection
          id="orchestration"
          label="Orchestration"
          title="One approach. Every system."
          intro={
            <>
              <p>Finance data lives in many places. Most tools fit one system well, or many systems badly.</p>
              <p>Sidwell reads your real columns and builds the analysis from them. No setup. No lock-in.</p>
            </>
          }
        >
          <h3 className="label mb-2 font-sans text-glass">Two Planning Paths, One Deterministic Core</h3>
          <p className="type-body mb-5 text-glass">
            Every question is routed before a single row is touched. The router&rsquo;s only job is deciding which planning path to use:
          </p>
          <div role="group" aria-label="Planning paths" className="border-y border-wall/25">
            {PATHS.map((p, idx) => (
              <div key={p.id} className="border-b border-wall/15 last:border-b-0">
                <PickRow on={activePath === idx} onClick={() => setActivePath(idx)}>
                  <span className="flex flex-wrap items-baseline justify-between gap-x-4">
                    <span className="font-semibold">{p.title}</span>
                    <span className="label text-glass">{p.badge}</span>
                  </span>
                  <span className="mt-1 block max-w-[70ch] text-[15px] text-glass">{p.detail}</span>
                </PickRow>
              </div>
            ))}
          </div>
          <CodePanel
            className="mt-6"
            title={`router.py — ${PATHS[activePath].title}`}
            meta="Router decision"
            code={PATHS[activePath].codeSnippet}
            footer={
              <>
                <span>Target: Engine compilation</span>
                <span>Guarantees: 100% deterministic</span>
              </>
            }
          />
          <h3 className="label mb-3 mt-10 font-sans text-glass">Supported Analysis Types (Both Paths)</h3>
          <ul className="m-0 grid list-none grid-cols-2 gap-x-7 border-b border-wall/20 p-0 sm:grid-cols-4">
            {ANALYSIS_TYPES.map((type) => (
              <li key={type} className="border-t border-wall/20 py-2.5 font-mono text-[13px]">{type}</li>
            ))}
          </ul>
          <p className="type-body mt-6 text-glass">
            <strong className="font-semibold text-wall">The result:</strong> You can ask questions against your Postgres GL today, swap to Snowflake in six months, and the same question works without reconfiguration. Finance data is data; the analysis stays the same.
          </p>
        </DocSection>

        <DocSection
          id="semantic-layer"
          label="The Semantic Layer"
          title="Columns labelled for you."
          intro={
            <>
              <p>Most tools need an engineer to label every column. It takes months and breaks when tables change.</p>
              <p>Sidwell labels columns when you connect. Add a table and ask right away.</p>
            </>
          }
        >
          <Statement title="Truly Schema-Agnostic" shadow={false} footer="Dynamic Column Pattern Classifier + Statistical Profiler">
            <p className="border-t border-wall/70 px-5 py-5 text-glass sm:px-7">
              This is what makes you truly schema-agnostic: Same planner, same interface, different GL. Whether it&rsquo;s QuickBooks, Xero, NetSuite, or a custom ledger—if your data is in a database, questions work out of the box.
            </p>
          </Statement>
          <div role="group" aria-label="Semantic roles" className="mt-8 grid grid-cols-1 border-t border-wall/25 md:grid-cols-2">
            {SEMANTIC_ROLES.map((r, idx) => (
              <div key={r.role} className="border-b border-wall/15 md:[&:nth-child(2n+1)]:border-r">
                <PickRow on={activeRole === idx} onClick={() => setActiveRole(idx)}>
                  <span className="flex flex-wrap items-baseline justify-between gap-x-4">
                    <span className="type-h3 !text-[22px]">{r.role}</span>
                    <span className="label text-glass">{r.badge}</span>
                  </span>
                  <span className="mt-1 block break-words font-mono text-[13px]">{r.examples}</span>
                  <span className="mt-1.5 block text-[15px] leading-snug text-glass">{r.desc}</span>
                </PickRow>
              </div>
            ))}
          </div>
          <p key={activeRole} className="swap-in mt-5 border-l-2 border-bronze bg-white px-4 py-3 text-[15px]">
            <strong className="mr-2 font-semibold">{SEMANTIC_ROLES[activeRole].role}</strong>
            <span className="text-glass">{SEMANTIC_ROLES[activeRole].example}</span>
          </p>
        </DocSection>

        <DocSection
          id="auditability"
          label="Auditability & Governance"
          title="The Audit Trail Every CFO Demands"
          intro={
            <>
              <p>Answer, calculation and proof. Together.</p>
              <p>The AI reads your question and writes the summary. The engine does the arithmetic. Pick a part below to see it in the receipt.</p>
            </>
          }
        >
          <div role="group" aria-label="Parts of a response" className="grid grid-cols-1 border-t border-wall/25 md:grid-cols-2">
            {RESPONSE_COMPONENTS.map((item, idx) => (
              <div key={item.key} className="border-b border-wall/15 md:[&:nth-child(2n+1)]:border-r">
                <PickRow on={activeComponent === idx} onClick={() => setActiveComponent(idx)}>
                  <span className="flex flex-wrap items-baseline justify-between gap-x-4">
                    <span className="label text-bronze-deep">{item.badge}</span>
                    <span className="text-[13px] text-glass">{item.key}</span>
                  </span>
                  <span className="mt-1.5 block font-semibold">{item.title}</span>
                  <span className="mt-1 block text-[15px] leading-snug text-glass">{item.desc}</span>
                </PickRow>
              </div>
            ))}
          </div>

          <div className="on-dark mt-8 bg-wall text-limestone">
            <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2 border-b border-limestone/20 px-5 py-5 sm:px-7">
              <div>
                <Label dark muted className="mb-1">Investigation Receipt Specimen</Label>
                <h3 className="type-h3">Full Lineage &amp; Execution Proof</h3>
              </div>
              <span className="text-[14px] font-semibold text-bronze-light">Audit Trail Attached</span>
            </div>
            <div className={`border-b border-limestone/20 px-5 py-4 transition-colors sm:px-7 ${lit('Text')}`}>
              <Label dark muted className="mb-1.5">Narrative (grounded, zero fabricated figures)</Label>
              <p>{NARRATIVE_EXAMPLE}</p>
            </div>
            <dl className="m-0 grid grid-cols-1 md:grid-cols-2">
              {AUDIT_METADATA_ITEMS.map((meta) => (
                <div key={meta.label} className={`border-b border-limestone/20 px-5 py-4 transition-colors sm:px-7 md:[&:nth-child(2n+1)]:border-r ${lit(meta.component)}`}>
                  <dt className="label mb-1 text-glass-light">{meta.label}</dt>
                  <dd className="m-0">
                    <span className="block break-words font-mono text-[14px]">{meta.value}</span>
                    <span className="mt-1 block text-[14px] text-glass-light">{meta.hint}</span>
                  </dd>
                </div>
              ))}
            </dl>
            <div className={`transition-colors ${lit('Pushdown SQL')}`}>
              <CodePanel className="!bg-transparent" title="Verified Pushdown SQL" code={VERIFIED_SQL} language="sql" />
            </div>
            <p className="border-t border-limestone/20 px-5 py-5 text-glass-light sm:px-7">
              Finance teams get the speed of AI without the &ldquo;trust me&rdquo; problem. You can show your CFO exactly where every number came from. You can hand this to an external auditor and they can verify the calculation.
            </p>
          </div>
        </DocSection>

        <DocSection
          id="tenancy"
          label="Tenancy & Security"
          title="Locked down."
          intro={
            <>
              <p>Sign in with Google or email. Every dataset and result is limited to your firm.</p>
              <p>Passwords and keys are encrypted and never shown again.</p>
            </>
          }
        >
          <Statement title="100% Query Audit Logging" shadow={false}>
            <p className="border-t border-wall/70 px-5 py-4 text-glass sm:px-7">
              Every query is logged. Every authorization check is recorded. Finance ops and compliance teams get full visibility.
            </p>
          </Statement>
          <div className="mt-8 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
            <h3 className="label font-sans text-glass">Platform Data Model &amp; Tenancy Hierarchy</h3>
            <span className="label text-bronze-deep">Fernet encrypted</span>
          </div>
          <div className="mt-3 border-t border-wall/70">
            {METADATA_ENTITIES.map((ent, idx) => (
              <Accordion key={ent.name} title={ent.name} detail={ent.detail} defaultOpen={idx === 0} headingLevel="h4" />
            ))}
          </div>
        </DocSection>

        <DocSection id="where-we-are" label="Where We Are" title="Where we’re going." intro="From exact answers today to a continuous close. Step through the plan.">
          <ol className="m-0 grid list-none grid-cols-1 border-t-2 border-wall p-0 sm:grid-cols-4">
            {ROADMAP_PHASES.map((p, idx) => (
              <li key={p.phase} className="border-b border-wall/15 sm:border-b-0 sm:border-r sm:last:border-r-0">
                <PickRow current on={idx === activePhase} onClick={() => setActivePhase(idx)}>
                  <span className="block font-semibold leading-snug">{p.phase}</span>
                  <span className="mt-1 block text-[12.5px] font-semibold tracking-[0.06em] text-glass">{p.status}</span>
                </PickRow>
              </li>
            ))}
          </ol>
          <div key={phase.phase} aria-live="polite" className="swap-in border-y border-wall/25 bg-white px-5 py-6 sm:px-7">
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <h3 className="type-h3">{phase.phase}</h3>
              <span className={`label ${phase.status === 'COMPLETED' ? 'text-patina' : 'text-glass'}`}>{phase.status}</span>
            </div>
            <ul className="m-0 list-none p-0">
              {phase.items.map((item) => (
                <li key={item} className="flex items-start gap-3 border-t border-stone py-3">
                  {phase.status === 'COMPLETED' ? (
                    <Icon name="check" size={18} className="mt-1 text-patina" />
                  ) : (
                    <Icon name="arrow" size={18} className="mt-1 text-glass" />
                  )}
                  <span className="min-w-0">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </DocSection>

        <DocSection id="market-positioning" label="Market Positioning" title="What Makes This Different" intro="Why teams choose Sidwell over BI tools and generic AI.">
          <div className="grid grid-cols-1 gap-x-10 gap-y-12 xl:grid-cols-2">
            <Compare
              eyebrow="Flexibility & Longevity"
              title="Why Schema-Agnostic Matters for Finance"
              intro="Traditional BI tools force you to choose between two unacceptable tradeoffs:"
              items={[
                { title: '1. Specialized (Adaptive, SAP Analytics)', detail: 'Fast and accurate, but locked into one GL flavor. Switching platforms means rebuilding everything from scratch.' },
                { title: '2. Generic (Tableau, Looker)', detail: 'Flexible but query performance degrades, variance investigations need manual tuning, and there is zero finance-domain understanding.' },
              ]}
              callout="Sidwell Converges These"
              calloutBody="Finance-domain rigor (understanding GL structure, account hierarchies, budget semantics) + schema-agnostic flexibility (works across any database, any GL)."
              result="Your finance team is never locked into one tool. Move data sources, change GL systems, or add connectors without breaking analysis."
            />
            <Compare
              eyebrow="Auditability & Trust"
              title="Why Deterministic Matters for Compliance"
              intro="Most AI-powered finance tools use LLMs for calculation. That is a fatal compliance flaw:"
              items={[
                { title: 'The LLM Arithmetic Hallucination Risk', detail: 'You cannot audit it, you cannot reproduce it, and external auditors will reject the output every time numbers drift.' },
                { title: 'The Deterministic Engine Guarantee', detail: 'Sidwell uses LLMs for understanding questions and explaining results—but the actual calculation is deterministic SQL and Python. Every number is auditable, reproducible, and defensible.' },
              ]}
              callout="The Enterprise Finance Gap"
              calloutBody="This is the gap in the market: AI that corporate finance and external auditors can actually use and verify."
              result="Total compliance readiness. Pass audit reviews without scrambling to reconstruct spreadsheet logic."
            />
          </div>
        </DocSection>
      </TechDocLayout>

      <CtaSection
        image="entrance"
        eyebrow="Get Started"
        title="See it work."
        subtitle="A live walkthrough on your data."
        primary={{ label: 'Request a demo', to: '/#request-briefing' }}
        secondary={{ label: 'Back to Overview', to: '/' }}
      />
    </main>
  );
}

export default Innovation;
